import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';
import { isEqual } from 'lodash-es';
import type { ScreenerQuestionContextQuestion } from '../../../__generated__/graphql-base-types';
import type { EventLogger } from '../../../lib/eventLogger/eventLogger.client';
import type { AnswerValue } from './screenerQuestionUtils';

const FIELD_FAMILY = 'SCREENER_QUESTIONS';
const QUESTION_TYPE = 'SCREENER_QUESTION';

export interface EmitScreenerFieldEditsParams {
    eventLogger: EventLogger;
    iaUid: string | undefined;
    draftId: string;
    currentPage: string;
    questions: ScreenerQuestionContextQuestion[];
    loadedAnswers: Record<string, AnswerValue>;
    answers: Record<string, AnswerValue>;
}

const isPrimitive = (value: unknown): value is string | number | boolean =>
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const trimStrings = (value: string | number | boolean): string | number | boolean =>
    typeof value === 'string' ? value.trim() : value;

/**
 * Mirrors `isSemanticallyEqual` in smart-apply-common's `prefillDeclined.ts` so the five mosaic
 * field-edit emitters and this one share one notion of "same". Change both together.
 */
const isSemanticallyEqual = <T>(a: T, b: T): boolean => {
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        if (a.every(isPrimitive) && b.every(isPrimitive)) {
            const left = a.map(trimStrings).sort();
            const right = b.map(trimStrings).sort();
            return left.every((value, index) => value === right[index]);
        }
    }
    if (typeof a === 'string' && typeof b === 'string') {
        return a.trim() === b.trim();
    }
    return isEqual(a, b);
};

export const emitScreenerFieldEdits = ({
    eventLogger,
    iaUid,
    draftId,
    currentPage,
    questions,
    loadedAnswers,
    answers,
}: EmitScreenerFieldEditsParams): void => {
    if (!iaUid) return;

    questions
        .filter(
            (question) =>
                !isSemanticallyEqual(loadedAnswers[question.name], answers[question.name]),
        )
        .forEach((question) => {
            eventLogger.logReviewFieldEdit?.({
                currentPage,
                draftId,
                iaUid,
                fieldFamily: FIELD_FAMILY,
                fieldName: question.id,
                questionType: AvroOptional.string(QUESTION_TYPE),
                questionId: AvroOptional.string(question.id),
            });
        });
};
